import * as React from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Bike, LogOut, Menu, Moon, Sun, X } from 'lucide-react';
import { ROLE_LABELS } from '@hch/shared';
import { Button } from '~/components/ui/button';
import {
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Separator,
} from '~/components/ui/primitives';
import { useAuth } from '~/lib/auth';
import { cn, fullName, initials } from '~/lib/utils';

export interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Correspondance exacte : evite qu'un onglet racine reste actif sur ses enfants. */
  end?: boolean;
}

/**
 * Coquille applicative des espaces authentifies.
 *
 * La navigation est une barre laterale sur grand ecran et un tiroir sur mobile. Le
 * choix compte : l'espace technicien s'utilise sur telephone, en exterieur — la
 * navigation doit disparaitre pour laisser toute la place au contenu, et revenir
 * d'un seul appui sur une cible large.
 */
export function AppShell({
  items,
  title,
  children,
}: {
  items: NavItem[];
  title: string;
  children: React.ReactNode;
}) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const location = useLocation();

  // Le tiroir se referme a chaque navigation : sans cela, il masquerait la page
  // que l'utilisateur vient d'ouvrir.
  React.useEffect(() => setMenuOpen(false), [location.pathname]);

  return (
    <div className="flex min-h-dvh bg-background">
      {/* Lien d'evitement : premiere tabulation de la page (RGAA 12.7). */}
      <a
        href="#contenu-principal"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground"
      >
        Aller au contenu principal
      </a>

      {menuOpen ? (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setMenuOpen(false)}
          aria-hidden
        />
      ) : null}

      <aside
        className={cn(
          'bg-sidebar border-sidebar-border fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r transition-transform lg:static lg:translate-x-0',
          menuOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-14 items-center justify-between px-4">
          <Link to="/" className="flex items-center gap-2 font-semibold">
            <Bike className="size-5" aria-hidden />
            <span>HomeCycl'Home</span>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMenuOpen(false)}
            aria-label="Fermer le menu"
          >
            <X />
          </Button>
        </div>

        <Separator />

        <nav className="flex-1 overflow-y-auto scroll-thin p-2" aria-label="Navigation principale">
          <p className="app-sidebar-section-label">{title}</p>
          <ul className="flex flex-col gap-0.5">
            {items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    isActive ? 'app-sidebar-item-active' : 'app-sidebar-item'
                  }
                >
                  <item.icon className="size-4 shrink-0" aria-hidden />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="bg-background/95 sticky top-0 z-20 flex h-14 items-center gap-2 border-b border-border px-4 backdrop-blur">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMenuOpen(true)}
            aria-label="Ouvrir le menu"
          >
            <Menu />
          </Button>

          <div className="flex-1" />

          <ThemeToggle />
          <UserMenu />
        </header>

        <main id="contenu-principal" className="flex min-w-0 flex-1 flex-col">
          {children}
        </main>
      </div>
    </div>
  );
}

/** Bascule clair/sombre, memorisee dans le navigateur. */
export function ThemeToggle() {
  const [dark, setDark] = React.useState(() => document.documentElement.classList.contains('dark'));

  const toggle = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('hch-theme', next ? 'dark' : 'light');
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggle}
      aria-label={dark ? 'Passer en theme clair' : 'Passer en theme sombre'}
    >
      {dark ? <Sun /> : <Moon />}
    </Button>
  );
}

export function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // La garde de route renvoie deja vers /connexion quand la session disparait, mais
  // seulement sur les ecrans qu'elle protege. Se deconnecter depuis une page PUBLIQUE
  // ne declenchait donc aucun changement d'ecran : le menu se vidait, et l'on restait
  // sur place. On ramene explicitement a l'accueil, ce qui donne le meme resultat
  // visible dans les deux cas.
  const seDeconnecter = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  if (!user) {
    return (
      <Button asChild size="sm" variant="outline">
        <Link to="/connexion">Se connecter</Link>
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Menu du compte">
          <Avatar>
            <AvatarFallback>{initials(user)}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <span className="block truncate font-medium text-foreground">{fullName(user)}</span>
          <span className="block truncate text-xs font-normal">{user.email}</span>
          <span className="mt-1 block text-xs font-normal">{ROLE_LABELS[user.role]}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/mon-compte/profil">Ma fiche</Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={() => void seDeconnecter()}>
          <LogOut aria-hidden />
          Se deconnecter
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
